import React, { useState } from 'react';
import { useTranslation } from '@/i18n';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { Text, type TextVariant } from '@/components/ui/text/Text';
import { Card } from '@/components/ui/cards/Card';
import { IconScout, IconScoutName } from '@/components/ui/images/IconScout';
import { colors } from '@/assets/styles/colors';


export type ConfirmModalVariant = 'default' | 'success' | 'alert' | 'error';
export type ConfirmModalButtonVariant = 'primary' | 'success' | 'alert' | 'error';

export interface ConfirmModalProps {
  variant?: ConfirmModalVariant;
  icon?: IconScoutName;
  iconColor?: string;
  /** Tamaño (px) del ícono superior. @default 32 */
  iconSize?: number;
  confirmButtonVariant?: ConfirmModalButtonVariant;
  title: string;
  bannerIcon?: IconScoutName;
  bannerText?: string;
  bodyText: string;
  /** Color del texto del cuerpo. @default colors.grays.neutral33 */
  bodyTextColor?: string;
  onConfirm: () => void | Promise<void>;
  onCancel?: () => void;
  onClose?: () => void;
  confirmText?: string;
  cancelText?: string;
  isLoading?: boolean;
  isConfirmDisabled?: boolean;
  children?: React.ReactNode;
  cardClassName?: string;
  /** Variante de texto (tamaño/peso) de ambos botones. @default tamaño estándar del botón (16px) */
  buttonTextVariant?: TextVariant;
  /** Color de texto del botón confirmar, en todos sus estados. @default color del `colorVariant` */
  confirmButtonTextColor?: string;
}

const variantColors: Record<ConfirmModalVariant, { iconColor: string; buttonVariant: 'primary' | 'success' | 'alert' | 'error' }> = {
  default: { iconColor: colors.primary.auxiliar, buttonVariant: 'primary' },
  success: { iconColor: colors.feedback.success300, buttonVariant: 'success' },
  alert: { iconColor: colors.feedback.alert300, buttonVariant: 'alert' },
  error: { iconColor: colors.feedback.error300, buttonVariant: 'error' },
};

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  variant = 'default',
  icon,
  iconColor,
  iconSize = 32,
  confirmButtonVariant,
  title,
  bannerIcon = 'info',
  bannerText,
  bodyText,
  bodyTextColor,
  onConfirm,
  onCancel,
  onClose,
  confirmText,
  cancelText,
  isLoading: externalIsLoading = false,
  isConfirmDisabled = false,
  children,
  cardClassName,
  buttonTextVariant,
  confirmButtonTextColor,
}) => {
  const { t: translate } = useTranslation();
  const [internalLoading, setInternalLoading] = useState(false);

  // Use external loading state if provided, otherwise use internal state
  const isLoading = externalIsLoading || internalLoading;

  const isConfirmButtonDisabled = isConfirmDisabled || isLoading;

  const handleConfirm = async () => {
    setInternalLoading(true);
    try {
      await onConfirm();
      if (onClose) {
        onClose();
      }
    } finally {
      setInternalLoading(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    if (onClose) {
      onClose();
    }
  };

  const resolvedIconColor = iconColor ?? variantColors[variant].iconColor;
  const buttonVariant = confirmButtonVariant ?? variantColors[variant].buttonVariant;
  const confirmButtonCustomVariant = confirmButtonTextColor
    ? {
      default: { textColor: confirmButtonTextColor },
      hover: { textColor: confirmButtonTextColor },
      pressed: { textColor: confirmButtonTextColor },
      focus: { textColor: confirmButtonTextColor },
    }
    : undefined;

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Card
        elevation={2}
        padding="24px"
        borderRadius="16px"
        data-testid="confirm-modal"
        className={`flex flex-col gap-y-4 max-w-[370px] ${cardClassName ?? ''}`}
      >
        {icon && (
          <div className="flex justify-center" data-testid="confirm-modal-icon-container">
            <IconScout
              name={icon}
              size={iconSize}
              color={resolvedIconColor}
              data-testid="confirm-modal-icon"
            />
          </div>
        )}

        <Text
          variant="header-6"
          color={colors.primary.main}
          className="text-center"
          data-testid="confirm-modal-title"
        >
          {title}
        </Text>

        {bannerIcon && bannerText && (
          <div
            className="flex items-center gap-2 py-2 px-4"
            style={{ backgroundColor: colors.primary.background200 }}
            data-testid="confirm-modal-banner"
          >
            <IconScout
              name={bannerIcon}
              size={24}
              color={colors.important.intermediate}
              data-testid="confirm-modal-banner-icon"
            />
            <Text
              variant="small-regular"
              color={colors.grays.neutral33}
              data-testid="confirm-modal-banner-text"
            >
              {bannerText}
            </Text>
          </div>
        )}

        <Text
          variant="subheader-regular"
          color={bodyTextColor ?? colors.grays.neutral33}
          className=""
          data-testid="confirm-modal-body"
        >
          {bodyText}
        </Text>

        {children}

        <div className="flex justify-center gap-4">
          <PrimaryButton
            onClick={handleCancel}
            disabled={isLoading}
            variant="outline"
            textVariant={buttonTextVariant}
            data-testid="confirm-modal-cancel-button"
          >
            {cancelText || translate('common:actions.cancel')}
          </PrimaryButton>

          <PrimaryButton
            onClick={handleConfirm}
            isLoading={isLoading}
            colorVariant={buttonVariant}
            disabled={isConfirmButtonDisabled}
            textVariant={buttonTextVariant}
            customVariant={confirmButtonCustomVariant}
            data-testid="confirm-modal-confirm-button"
          >
            {confirmText || translate('common:actions.confirm')}
          </PrimaryButton>
        </div>
      </Card>
    </div>
  );
};

export default ConfirmModal;
